import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBuilding,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaLock,
  FaMapMarkerAlt,
  FaTruck,
  FaIdCard,
  FaRoad
} from "react-icons/fa";
import { useAuth } from "../context/AuthContext";

const Register = () => {
  const navigate = useNavigate();
  const { registerCompany, registerDriver, loading, error } = useAuth();

  const colors = {
    primary: "#e28743",
    secondary: "#304a3f",
    light: "#d1d5db",
    danger: "#ef4444"
  };

  const [role, setRole] = useState("company");

  const [companyData, setCompanyData] = useState({
    companyName: "",
    companyEmail: "",
    companyPhone: "",
    registrationNumber: "",
    companyType: "transportation",
    companyAddress: {
      street: "",
      city: "",
      state: "",
      country: "Nigeria"
    },
    adminName: "",
    adminEmail: "",
    adminPassword: "",
    adminPhone: ""
  });

  const [driverData, setDriverData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    licenseNumber: "",
    driverType: "truck",
    experience: "",
    location: ""
  });

  const handleCompanyChange = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setCompanyData((prev) => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value }
      }));
    } else {
      setCompanyData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDriverChange = (e) => {
    const { name, value } = e.target;
    setDriverData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result =
      role === "company"
        ? await registerCompany(companyData)
        : await registerDriver(driverData);

    if (result?.success) {
      navigate("/dashboard");
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        background: `linear-gradient(135deg, ${colors.secondary}, ${colors.primary})`
      }}
    >
      <div className="max-w-4xl w-full bg-white/10 backdrop-blur-lg rounded-xl p-8">
        <h1 className="text-3xl font-bold text-white text-center mb-6">
          Register Account
        </h1>

        {/* Role Switch */}
        <div className="flex justify-center mb-8">
          <button
            onClick={() => setRole("company")}
            className={`px-6 py-2 rounded-l-lg font-bold ${
              role === "company"
                ? "bg-white text-black"
                : "bg-white/20 text-white"
            }`}
          >
            Company
          </button>
          <button
            onClick={() => setRole("driver")}
            className={`px-6 py-2 rounded-r-lg font-bold ${
              role === "driver"
                ? "bg-white text-black"
                : "bg-white/20 text-white"
            }`}
          >
            Driver
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* ================= COMPANY REGISTRATION ================= */}
          {role === "company" && (
            <>
              <h2 className="text-xl text-white font-bold flex items-center">
                <FaBuilding className="mr-2" /> Company Information
              </h2>

              <input
                type="text"
                name="companyName"
                placeholder="Company Name"
                required
                onChange={handleCompanyChange}
                className="input"
              />

              <input
                type="text"
                name="registrationNumber"
                placeholder="Registration Number"
                required
                onChange={handleCompanyChange}
                className="input"
              />

              <input
                type="email"
                name="companyEmail"
                placeholder="Company Email"
                required
                onChange={handleCompanyChange}
                className="input"
              />

              <input
                type="tel"
                name="companyPhone"
                placeholder="Company Phone"
                required
                onChange={handleCompanyChange}
                className="input"
              />

              <h3 className="text-lg text-white font-bold flex items-center">
                <FaUser className="mr-2" /> Admin Account
              </h3>

              <input
                type="text"
                name="adminName"
                placeholder="Admin Name"
                required
                onChange={handleCompanyChange}
                className="input"
              />

              <input
                type="email"
                name="adminEmail"
                placeholder="Admin Email"
                required
                onChange={handleCompanyChange}
                className="input"
              />

              <input
                type="password"
                name="adminPassword"
                placeholder="Admin Password"
                required
                minLength={6}
                onChange={handleCompanyChange}
                className="input"
              />
            </>
          )}

          {/* ================= DRIVER REGISTRATION ================= */}
          {role === "driver" && (
            <>
              <h2 className="text-xl text-white font-bold flex items-center">
                <FaTruck className="mr-2" /> Driver Registration
              </h2>

              <input
                type="text"
                name="name"
                placeholder="Full Name"
                required
                onChange={handleDriverChange}
                className="input"
              />

              <input
                type="email"
                name="email"
                placeholder="Email"
                required
                onChange={handleDriverChange}
                className="input"
              />

              <input
                type="password"
                name="password"
                placeholder="Password"
                required
                minLength={6}
                onChange={handleDriverChange}
                className="input"
              />

              <input
                type="tel"
                name="phone"
                placeholder="Phone Number"
                onChange={handleDriverChange}
                className="input"
              />

              <input
                type="text"
                name="licenseNumber"
                placeholder="Driver License Number"
                required
                onChange={handleDriverChange}
                className="input"
              />

              <input
                type="number"
                name="experience"
                placeholder="Years of Experience"
                onChange={handleDriverChange}
                className="input"
              />

              <input
                type="text"
                name="location"
                placeholder="Location"
                onChange={handleDriverChange}
                className="input"
              />
            </>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-500/20 text-red-400 p-3 rounded">
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 rounded-lg font-bold text-white transition hover:scale-105"
            style={{ backgroundColor: colors.primary }}
          >
            {loading ? "Processing..." : "Create Account"}
          </button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="text-sm text-white hover:underline"
            >
              Already have an account? Login
            </button>
          </div>
        </form>
      </div>

      {/* Input Styles */}
      <style>
        {`
          .input {
            width: 100%;
            padding: 12px;
            border-radius: 8px;
            background: rgba(255,255,255,0.15);
            color: white;
            outline: none;
          }
          .input::placeholder {
            color: #d1d5db;
          }
        `}
      </style>
    </div>
  );
};

export default Register;
